import { AdType } from '@/modules/adv/types'

//#region GA events types
export type EventGA = 'custom_event' | 'interface_event' | 'view_search_results'
export type InterfaceGAEvents =
    | 'interface_click'
    | 'interface_show'
    | 'interface_hide'
    | 'interface_rotate'
    | 'interface_scroll'
    | 'pwa_native_install_prompt_shown'
    | 'pwa_portal_install_prompt_shown'
    | 'pwa_portal_install_game_prompt_shown'
    | 'pwa_portal_install_game_prompt_shown_toast'
    | 'pwa_portal_install_prompt_shown_toast'

export type CTREvents = 'click' | 'impression'

export type LogSource = 'ad-sdk' | 'wrap' | 'default'

export const GAMEPLAY_EVENT_THRESHOLDS_MS = {
    game: 0,
    game_30s: 30_000,
    game_60s: 60_000,
} as const

export const ADJUST_LOG_TOKENS: Partial<Record<AvailablePGEvents, string>> = {
    game: 'yr6hfz',
    game_30s: 'f4nmqi',
    playgame_5min: 'sesdsp',
}

export const ADJUST_TOKENS: Partial<Record<AvailablePGEvents, string>> = {
    ...ADJUST_LOG_TOKENS,
    purchase_successful: '43vpyb',
}

export type GameplayEventName = keyof typeof GAMEPLAY_EVENT_THRESHOLDS_MS

/* FOR GA events please don't use following names https://support.google.com/analytics/answer/9234069?hl=ru
  as it can lead to collecting data hell
*/
export type AvailableEvent =
    | 'ad_blocker_detect_attempt'
    | 'ad_blocker_offer'
    | 'ad_blocker_offer_fail'
    | 'ad_blocker_offer_skip'
    | 'ad_blocker_offer_success'
    | 'ad_click'
    | 'ad_close'
    | 'ad_empty'
    | 'ad_hb_auction'
    | 'ad_hb_auction_cache'
    | 'ad_hb_auction_timeout'
    | 'ad_hb_bidder_request'
    | 'ad_hb_bidder_response'
    | 'ad_hb_invalid_format_bid'
    | 'ad_hb_render_failed'
    | 'ad_hb_user_ids'
    | 'ad_rendered'
    | 'ad_request'
    | 'ad_request_attempt_failed'
    | 'ad_request_failed'
    | 'ad_request_unexpected_error'
    | 'ad_service_ready'
    | 'ad_show'
    | 'ad_show_high_value'
    | 'ad_show_not_called_after_ready'
    | 'ad_show_midroll'
    | 'ad_skipped'
    | 'ad_unexpected_behavior'
    | 'ad_waterfall_attempt'
    | 'audience_detected'
    | 'audience_lost'
    | 'claim'
    | 'close_suggested_game_popup'
    | 'cls_high'
    | 'consent_change'
    | 'consent_init'
    | 'do-task'
    | 'export-init-click'
    | 'game'
    | 'game_15s'
    | 'game_30s'
    | 'game_60s'
    | 'game_bridge_loaded'
    | 'game_preloader_timeout'
    | 'game_preview'
    | 'in_game_ad_not_requested'
    | 'invite-friend'
    | 'login'
    | 'ms_store_url_opened'
    | 'native_app_promo_rating_show'
    | 'open_purchase_window'
    | 'play'
    | 'play_suggested_game'
    | 'playgame_10min'
    | 'playgame_3min'
    | 'playgame_5min'
    | 'portal_session_start'
    | 'preroll_end'
    | 'pwa_native_install_accepted'
    | 'share'
    | 'show_suggested_game_popup'
    | 'sign-in'
    | 'sign-out'
    | 'suggested_game_from_unavailable_game_click'
    | 'timespent_ltv'
    | 'vue_message'
    | 'view_search_results'
    | CTREvents
    | InterfaceGAEvents

type CommonEventGA = {
    event: EventGA
    eventName: AvailableEvent
    clid?: 'direct' | string
    widget_id: string
    route_name?: string
    experiments?: string
}

export type InterfaceEventGA = CommonEventGA & {
    interface_target: string
    interface_path: string
}

export type SearchEventGA = CommonEventGA & {
    search_term: string
    search_result_count: number
}

export type CustomEventForGa = CommonEventGA & {
    action: string
    // TODO separate ad events
    ad_placement: string
    game_provider: string
    game_categories: string[]
    ad_unit: string
    instream_duration: number | null
    watched_duration: number | null
    waterfall_index: number | null
    error_message: string
    amount_in_game_currency: number | null
}

export type EventForGa = CustomEventForGa | InterfaceEventGA | SearchEventGA

//#endregion

export type VoteEvent = 'reaction_like' | 'reaction_dislike' | 'reaction_unset_like' | 'reaction_unset_dislike'
export type AuthEvent = 'auth_success' | 'auth_failed'
export type AuthPopupEvent = 'auth_popup_opened' | 'auth_popup_closed'
export type PaymentPopupEvent =
    | 'payment_modal_opened'
    | 'purchase_successful'
    | 'purchase_fail'
    | 'new_subscription_after_in_game_purchase'
export type PushSubscriptionEvent = 'push_notification_granted' | 'push_notification_denied'
export type GooglePlayModalEvent =
    | 'google_play_modal_opened'
    | 'google_play_modal_closed'
    | 'google_play_install_clicked'
    | 'google_play_app_opened'
export type LoopEvent = 'loop_session_start' | 'loop_play'

export type AvailablePGEvents =
    | AvailableEvent
    | CustomPGEvents
    | LoopEvent
    | VoteEvent
    | AuthEvent
    | AuthPopupEvent
    | PaymentPopupEvent
    | PushSubscriptionEvent
    | GooglePlayModalEvent
export type CustomPGEvents = 'game_opened' | 'ping' | 'navigation' | 'portal_session_end' | 'homepage_5s'
export type EventsType = EventGA | 'user_time'

// Atención!
// Before changing this type, you have to add columns to clickhouse, otherwise these fileds will get lost
type AdditionalEventProps = {
    dimension1: string
    dimension2: string
    dimension3: string
}

export type LoggerEvent = Partial<AdditionalEventProps> & {
    event: EventsType
    eventName: AvailablePGEvents
    action?: string
    adPlacement?: AdType
    adUnit?: string
    adProvider?: string
    adHbBidder?: string
    adHbCpmUsd?: number
    adHbRequestId?: string
    errorMessage?: string
    instreamDuration?: number
    watchedDuration?: number
    waterfallIndex?: number
    amountInGameCurrency?: number
    externaId?: string
    source?: LogSource
}

export type StartRumEvent = (init: {
    operationName: string
}) => (arg: {
    status: 'success' | 'error' | 'empty'
    dimension1?: string
    dimension2?: string
    dimension3?: string
    dimension4?: string
}) => void
